<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: SurnameTree                                           |
| Section: components                                              |
| Purpose: Interactive surname tree chart with zoom and focus.     |
+------------------------------------------------------------------+
-->
<template>
    <div class="surname-tree-container">
        <div id="zoom-buttons">
            <button @click="zoomIn"><img src="@/assets/icons/zoom_in_red.svg" alt="Zoom In"></button>
            <button @click="zoomOut"><img src="@/assets/icons/zoom_out_red.svg" alt="Zoom Out"></button>
        </div>
        <button id="fullscreen-button" @click="toggleFullscreen"
            :title="isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'">
            <img v-if="!isFullscreen" src="@/assets/icons/fullscreen.svg" alt="Volledig scherm">
            <img v-else src="@/assets/icons/fullscreen_exit.svg" alt="Volledig scherm verlaten">
        </button>
        <button id="toggle-sidebar-button" @click="toggleSidebar" :class="{ 'sidebar-collapsed': sidebarCollapsed }"
            :title="sidebarCollapsed ? 'Achternamen tonen' : 'Achternamen verbergen'">
            <img v-if="sidebarCollapsed" src="@/assets/icons/chevron_right.svg" alt="Toon">
            <img v-else src="@/assets/icons/chevron_left.svg" alt="Verberg">
        </button>
        <div class="surname-list-container" :class="{ collapsed: sidebarCollapsed }">
            <h1>{{ uniqueLastnames }} {{ $t('nameOverview.heading') }}</h1>
            <div class="surname-list-search">
                <input v-model.trim="searchQuery" type="text" placeholder="Zoek achternaam..."
                    aria-label="Zoek achternaam">
            </div>
            <VirtualizedTreeList v-if="!sidebarCollapsed" ref="surnameList" :items="filteredNodeList" item-key="id"
                :estimated-row-height="40" :overscan="8" :initial-scroll-offset="listScrollOffset"
                aria-label="Achternamen" @scroll-offset="listScrollOffset = $event">
                <template #default="{ item: node }">
                    <div class="surname-item" :class="{ highlighted: node.id === selectedSurname }"
                        @click="selectSurname(node.id, false)">
                        <span class="surname-name" :title="node.id">{{ node.id }}</span>
                        <div class="dropdown">
                            <button class="dropdown-toggle">
                                <img src="@/assets/icons/dropdown_white.svg" aria-label="Acties">
                            </button>
                            <div class="dropdown-menu">
                                <button @click.stop="goToSearch(node.id)">{{ $t('nameOverview.dropdownSearch') }}</button>
                            </div>
                        </div>

                        <p class="secondary-label" v-if="node.id === selectedSurname && secondaryNodes.length">Directe
                            verwantschappen:</p>
                        <ul v-if="node.id === selectedSurname && secondaryNodes.length" class="secondary-list">
                            <li v-for="secondary in secondaryNodes" :key="secondary" class="secondary-item">
                                {{ secondary }}
                            </li>
                        </ul>

                        <p class="path-label" v-if="node.id === selectedSurname && pathNodes.length">Route naar de familie
                            Thümann:</p>
                        <ul v-if="node.id === selectedSurname && pathNodes.length" class="path-list">
                            <li v-for="pathNode in pathNodes" :key="pathNode" class="path-item">
                                {{ pathNode }}
                            </li>
                        </ul>
                    </div>
                </template>
            </VirtualizedTreeList>
        </div>
        <div id="surname-tree-container" :class="{ 'is-loading': loading || graphLoading }">
            <div v-if="loading || graphLoading" class="surname-tree-status surname-tree-status--overlay">
                <LoadingSpinner />
                <span>{{ loading ? 'Achternamen laden...' : 'Layout laden...' }}</span>
                <div class="surname-tree-progress">
                    <div class="surname-tree-progress__bar" role="progressbar" :aria-valuemin="0" :aria-valuemax="100"
                        :aria-valuenow="loaderProgress">
                        <div class="surname-tree-progress__fill" :style="{ width: `${loaderProgress}%` }"></div>
                    </div>
                    <span class="surname-tree-progress__value">{{ loaderProgress }}%</span>
                </div>
            </div>
            <div v-else-if="error" class="surname-tree-status surname-tree-status--error">{{ error }}</div>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';
import { getUniqueLastnames } from '@/api/personService';
import { fetchRuntimeJson } from '@/utils/runtimeJson';
import { useAuth } from '@/composables/useAuth';
import { hasLivingPersonAccess } from '@/utils/personPrivacy';
import VirtualizedTreeList from '@/components/VirtualizedTreeList.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import {
    TREE_VIEW_STATE_SCHEMA_VERSION,
    TREE_DATA_SNAPSHOT_VERSION,
    getViewportWorldCenter,
    isCompatibleTreeViewState,
    restoreTransformFromWorldCenter,
    selectViewportLabels,
} from '@/utils/treePerformance.mjs';

const HIDDEN_SURNAME = '[Inlandsche vrouw]';

export default {
    name: 'SurnameTree',
    components: { VirtualizedTreeList, LoadingSpinner },
    props: {
        initialState: {
            type: Object,
            default: null,
        },
    },
    emits: ['state-change'],
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    computed: {
        filteredNodeList() {
            const q = (this.searchQuery || '').trim().toLowerCase();
            if (!q) return this.nodeList;
            return this.nodeList.filter(node => (node.id || '').toLowerCase().includes(q));
        },
        privacyScope() {
            return hasLivingPersonAccess(this.authToken) ? 'member' : 'public';
        },
        loaderProgress() {
            if (this.loading && !this.graphLoading) return 5;
            return Math.max(0, Math.min(100, Math.round(this.graphProgress || 0)));
        },
    },
    data() {
        return {
            loading: true,
            error: null,
            graphLoading: false,
            graphProgress: 0,
            treeData: null,
            zoom: null,
            outerSvg: null,
            graphRoot: null,
            simulation: null,
            simNodes: [],
            simLinks: [],
            nodeSelection: null,
            linkSelection: null,
            labelSelection: null,
            selectedSurname: '',
            nodeList: [],
            secondaryNodes: [],
            pathNodes: [],
            uniqueLastnames: 0,
            dropdownOpen: null,
            sidebarCollapsed: true,
            isFullscreen: false,
            searchQuery: '',
            listScrollOffset: 0,
            graphWidth: 0,
            graphHeight: 0,
            layoutSettled: false,
            currentZoomK: 1,
            nodeDegreeMap: new Map(),
            positionRaf: null,
            resizeRaf: null,
        };
    },
    watch: {
        searchQuery() {
            if (this._restoringViewState) return;
            this.listScrollOffset = 0;
            this.$nextTick(() => this.$refs.surnameList?.scrollToKey(this.filteredNodeList[0]?.id, 'start'));
        },
    },
    mounted() {
        this.handleFullscreenChange();
        if (!this.restoreInitialState()) this.loadSurnameLinks();
        window.addEventListener('resize', this.resizeTree);
        window.addEventListener('click', this.closeDropdown);
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('msfullscreenchange', this.handleFullscreenChange);
    },
    beforeUnmount() {
        this.$emit('state-change', this.createStateSnapshot());
        window.removeEventListener('resize', this.resizeTree);
        window.removeEventListener('click', this.closeDropdown);
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('msfullscreenchange', this.handleFullscreenChange);
        this.teardownGraph();
    },
    methods: {
        restoreInitialState() {
            if (!isCompatibleTreeViewState(this.initialState, this.privacyScope, 'achternamen')) return false;
            const links = this.initialState?.data?.links;
            if (!Array.isArray(links)) return false;

            this._restoringViewState = true;
            const view = this.initialState.view || {};
            this.treeData = { links };
            this.uniqueLastnames = Number(this.initialState.data.uniqueLastnames) || 0;
            this.selectedSurname = view.selection || '';
            this.searchQuery = view.searchQuery || '';
            this.sidebarCollapsed = view.sidebarCollapsed !== false;
            this.listScrollOffset = Number(view.scrollOffset) || 0;
            this.loading = false;
            this.error = null;
            this.graphLoading = true;
            this.graphProgress = 8;
            this.$nextTick(() => {
                this._restoringViewState = false;
                this.createSurnameTree(this.treeData, this.initialState);
            });
            return true;
        },
        async loadSurnameLinks() {
            this.loading = true;
            this.error = null;
            try {
                const [treeData, uniqueLastnames] = await Promise.all([
                    fetchRuntimeJson('/data/getSurnameLinks/surnameLinks.json', null),
                    getUniqueLastnames().catch(() => 0),
                ]);
                if (!treeData) throw new Error('No surname links data available');
                this.treeData = {
                    links: this.normalizeLinks(treeData.links),
                };
                this.uniqueLastnames = Number(uniqueLastnames) || 0;
                this.loading = false;
                this.graphLoading = true;
                this.graphProgress = 8;
                this.$nextTick(() => this.createSurnameTree(this.treeData));
            } catch (error) {
                console.error('Error loading surname links:', error);
                this.loading = false;
                this.graphLoading = false;
                this.error = 'De achternamenstructuur kon niet worden geladen.';
            }
        },
        normalizeLinks(rawLinks) {
            return (Array.isArray(rawLinks) ? rawLinks : [])
                .map(link => ({
                    source: typeof link.source === 'object' ? link.source.id : link.source,
                    target: typeof link.target === 'object' ? link.target.id : link.target,
                }))
                .filter(link => link.source && link.target
                    && link.source !== HIDDEN_SURNAME
                    && link.target !== HIDDEN_SURNAME
                    && link.source !== link.target);
        },
        createStateSnapshot() {
            const transform = this.outerSvg?.node() ? d3.zoomTransform(this.outerSvg.node()) : d3.zoomIdentity;
            return {
                schemaVersion: TREE_VIEW_STATE_SCHEMA_VERSION,
                privacyScope: this.privacyScope,
                data: {
                    snapshotVersion: TREE_DATA_SNAPSHOT_VERSION,
                    links: this.normalizeLinks(this.treeData?.links),
                    uniqueLastnames: this.uniqueLastnames,
                    dataVersion: `${this.simNodes.length}:${this.simLinks.length}`,
                },
                layout: {
                    positions: this.simNodes.map(node => ({ id: node.id, x: node.x, y: node.y })),
                    settled: this.layoutSettled,
                    alpha: this.simulation?.alpha?.() || 0,
                },
                view: {
                    type: 'achternamen',
                    zoom: {
                        scale: transform.k,
                        worldCenter: getViewportWorldCenter(transform, this.graphWidth, this.graphHeight),
                    },
                    selection: this.selectedSurname,
                    searchQuery: this.searchQuery,
                    sidebarCollapsed: this.sidebarCollapsed,
                    scrollOffset: this.listScrollOffset,
                },
            };
        },
        zoomIn() {
            if (this.outerSvg && this.zoom) this.outerSvg.transition().duration(500).call(this.zoom.scaleBy, 1.5);
        },
        zoomOut() {
            if (this.outerSvg && this.zoom) this.outerSvg.transition().duration(500).call(this.zoom.scaleBy, 0.5);
        },
        resizeTree() {
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
                this.resizeRaf = null;
                if (!this.outerSvg?.node()) return;
                const container = this.$el.querySelector('#surname-tree-container');
                const previous = d3.zoomTransform(this.outerSvg.node());
                const worldCenter = getViewportWorldCenter(previous, this.graphWidth, this.graphHeight);
                this.graphWidth = container?.clientWidth || 0;
                this.graphHeight = container?.clientHeight || 0;
                this.outerSvg.attr('width', this.graphWidth).attr('height', this.graphHeight);
                const restored = restoreTransformFromWorldCenter(worldCenter, previous.k, this.graphWidth, this.graphHeight);
                this.outerSvg.call(this.zoom.transform, d3.zoomIdentity.translate(restored.x, restored.y).scale(restored.k));
                this.refreshVisibleLabels();
            });
        },
        toggleDropdown(nodeId) {
            this.dropdownOpen = this.dropdownOpen === nodeId ? null : nodeId;
        },
        closeDropdown() {
            this.dropdownOpen = null;
        },
        goToSearch(surname) {
            this.$router.push(`/search?q=${encodeURIComponent(surname)}`);
        },
        toggleSidebar() {
            this.sidebarCollapsed = !this.sidebarCollapsed;
        },
        toggleFullscreen() {
            const elem = document.documentElement;
            if (!this.isFullscreen) {
                if (elem.requestFullscreen) elem.requestFullscreen();
                else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
                else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
            } else if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            else if (document.msExitFullscreen) document.msExitFullscreen();
        },
        handleFullscreenChange() {
            this.isFullscreen = Boolean(document.fullscreenElement
                || document.webkitFullscreenElement
                || document.msFullscreenElement);
        },
        findPathToThuemann(fromSurname, links) {
            const targetSurnames = ['Thümann', 'Thumann'];
            if (targetSurnames.includes(fromSurname)) return { path: [fromSurname], targetReached: fromSurname };
            const graph = new Map();
            for (const link of links) {
                const source = link.source?.id || link.source;
                const target = link.target?.id || link.target;
                if (!graph.has(source)) graph.set(source, []);
                if (!graph.has(target)) graph.set(target, []);
                graph.get(source).push(target);
                graph.get(target).push(source);
            }
            const queue = [[fromSurname]];
            const visited = new Set([fromSurname]);
            while (queue.length) {
                const path = queue.shift();
                const current = path[path.length - 1];
                if (targetSurnames.includes(current)) return { path, targetReached: current };
                for (const neighbor of graph.get(current) || []) {
                    if (visited.has(neighbor)) continue;
                    visited.add(neighbor);
                    queue.push([...path, neighbor]);
                }
            }
            return { path: [], targetReached: null };
        },
        centerOnNode(nodeId) {
            const node = this.simNodes.find(candidate => candidate.id === nodeId);
            if (!node || !this.outerSvg || !this.zoom) return;
            const scale = 1.5;
            const x = this.graphWidth / 2 - node.x * scale;
            const y = this.graphHeight / 2 - node.y * scale;
            this.outerSvg.transition().duration(750)
                .call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale));
        },
        selectSurname(surname, fromGraph = false) {
            this.selectedSurname = this.selectedSurname === surname ? '' : surname;
            this.highlightSurname();
            if (this.selectedSurname) this.centerOnNode(this.selectedSurname);
            if (fromGraph && !this.sidebarCollapsed) {
                this.$nextTick(() => this.$refs.surnameList?.scrollToKey(surname, 'center'));
            }
        },
        createSurnameTree(data, restoreState = null) {
            this.teardownGraph();
            this.graphLoading = true;
            this.graphProgress = 10;
            const container = this.$el.querySelector('#surname-tree-container');
            this.graphWidth = container?.clientWidth || this.$el.clientWidth || 1;
            this.graphHeight = container?.clientHeight || this.$el.clientHeight || 1;

            const normalizedLinks = this.normalizeLinks(data?.links);
            const ids = [...new Set(normalizedLinks.flatMap(link => [link.source, link.target]))];
            const positions = new Map((restoreState?.layout?.positions || []).map(position => [position.id, position]));
            this.simNodes = ids.map((id) => {
                const position = positions.get(id);
                return {
                    id,
                    ...(position && Number.isFinite(position.x) && Number.isFinite(position.y)
                        ? { x: position.x, y: position.y }
                        : {}),
                };
            });
            this.simLinks = normalizedLinks.map(link => ({ ...link }));
            this.nodeList = [...this.simNodes].sort((a, b) => a.id.localeCompare(b.id, 'nl', { sensitivity: 'base' }));
            if (!this.uniqueLastnames) this.uniqueLastnames = this.nodeList.length;

            this.nodeDegreeMap = new Map(this.simNodes.map(node => [node.id, 0]));
            for (const link of normalizedLinks) {
                this.nodeDegreeMap.set(link.source, (this.nodeDegreeMap.get(link.source) || 0) + 1);
                this.nodeDegreeMap.set(link.target, (this.nodeDegreeMap.get(link.target) || 0) + 1);
            }

            this.zoom = d3.zoom()
                .scaleExtent([0.1, 8])
                .on('zoom', (event) => {
                    this.currentZoomK = event.transform.k;
                    this.graphRoot.attr('transform', event.transform);
                })
                .on('end', () => this.refreshVisibleLabels());

            this.outerSvg = d3.select(container)
                .append('svg')
                .attr('width', this.graphWidth)
                .attr('height', this.graphHeight)
                .call(this.zoom)
                .on('click', () => {
                    this.selectedSurname = '';
                    this.highlightSurname();
                });
            this.graphRoot = this.outerSvg.append('g');

            this.linkSelection = this.graphRoot.append('g')
                .attr('class', 'links')
                .selectAll('line')
                .data(this.simLinks)
                .join('line')
                .attr('stroke', '#aaa')
                .attr('stroke-width', 1);

            this.simulation = d3.forceSimulation(this.simNodes)
                .force('link', d3.forceLink(this.simLinks).id(node => node.id).distance(100))
                .force('charge', d3.forceManyBody().strength(-100))
                .force('center', d3.forceCenter(this.graphWidth / 2, this.graphHeight / 2))
                .alphaDecay(0.04)
                .alphaMin(0.02)
                .stop();

            this.nodeSelection = this.graphRoot.append('g')
                .attr('class', 'nodes')
                .selectAll('circle')
                .data(this.simNodes, node => node.id)
                .join('circle')
                .attr('data-id', node => node.id)
                .attr('r', 8)
                .attr('fill', '#ae1e25')
                .attr('stroke', 'white')
                .attr('stroke-width', 1)
                .call(d3.drag()
                    .on('start', (event, node) => {
                        if (!event.active) this.simulation.alphaTarget(0.3).restart();
                        node.fx = node.x;
                        node.fy = node.y;
                    })
                    .on('drag', (event, node) => {
                        node.fx = event.x;
                        node.fy = event.y;
                        this.schedulePositionPaint();
                    })
                    .on('end', (event, node) => {
                        if (!event.active) this.simulation.alphaTarget(0);
                        node.fx = null;
                        node.fy = null;
                    }))
                .on('click', (event, node) => {
                    event.stopPropagation();
                    this.selectSurname(node.id, true);
                });

            this.graphRoot.append('g').attr('class', 'labels');
            this.layoutSettled = Boolean(restoreState?.layout?.settled);
            this.simulation
                .on('tick', () => {
                    const alpha = this.simulation.alpha();
                    const progress = Math.max(10, Math.min(92, ((1 - alpha) / 0.98) * 92));
                    if (progress >= this.graphProgress + 2) this.graphProgress = Math.floor(progress);
                    this.schedulePositionPaint();
                })
                .on('end', () => this.finalizeLayout());

            if (this.layoutSettled && positions.size === this.simNodes.length) {
                this.simulation.stop().alpha(0);
                this.paintGraphPositions();
                this.restoreViewport(restoreState?.view?.zoom);
                this.finalizeLayout();
            } else {
                const restoredAlpha = Number(restoreState?.layout?.alpha);
                this.layoutSettled = false;
                this.simulation.alpha(Number.isFinite(restoredAlpha) && restoredAlpha > 0.02 ? restoredAlpha : 1).restart();
                if (restoreState?.view?.zoom) this.restoreViewport(restoreState.view.zoom);
            }
        },
        restoreViewport(zoomState) {
            if (!zoomState || !this.outerSvg || !this.zoom) return;
            const restored = restoreTransformFromWorldCenter(
                zoomState.worldCenter,
                zoomState.scale,
                this.graphWidth,
                this.graphHeight,
            );
            this.outerSvg.call(this.zoom.transform, d3.zoomIdentity.translate(restored.x, restored.y).scale(restored.k));
        },
        schedulePositionPaint() {
            if (this.positionRaf) return;
            this.positionRaf = requestAnimationFrame(() => {
                this.positionRaf = null;
                this.paintGraphPositions();
            });
        },
        paintGraphPositions() {
            this.linkSelection
                ?.attr('x1', link => link.source.x)
                .attr('y1', link => link.source.y)
                .attr('x2', link => link.target.x)
                .attr('y2', link => link.target.y);
            this.nodeSelection?.attr('cx', node => node.x).attr('cy', node => node.y);
        },
        finalizeLayout() {
            this.layoutSettled = true;
            if (this.positionRaf) cancelAnimationFrame(this.positionRaf);
            this.positionRaf = null;
            this.paintGraphPositions();
            this.highlightSurname();
            this.graphProgress = 100;
            requestAnimationFrame(() => {
                if (this.layoutSettled) this.graphLoading = false;
            });
        },
        getForcedLabelIds() {
            return new Set([
                this.selectedSurname,
                ...this.secondaryNodes,
                ...this.pathNodes,
            ].filter(Boolean));
        },
        refreshVisibleLabels() {
            if (!this.layoutSettled || !this.graphRoot || !this.outerSvg?.node()) return;
            const transform = d3.zoomTransform(this.outerSvg.node());
            const labelNodes = selectViewportLabels({
                nodes: this.simNodes,
                transform,
                width: this.graphWidth,
                height: this.graphHeight,
                forcedIds: this.getForcedLabelIds(),
                priorityForNode: node => this.nodeDegreeMap.get(node.id) || 0,
            });
            this.labelSelection = this.graphRoot.select('.labels')
                .selectAll('text')
                .data(labelNodes, node => node.id)
                .join(
                    enter => enter.append('text')
                        .attr('dy', '.35em')
                        .style('font-size', '12px')
                        .style('pointer-events', 'none')
                        .text(node => node.id),
                    update => update,
                    exit => exit.remove(),
                )
                .attr('x', node => node.x + 12)
                .attr('y', node => node.y);
            this.styleSurnameLabels();
        },
        styleSurnameLabels() {
            const pathNodes = new Set(this.pathNodes);
            const secondaryNodes = new Set(this.secondaryNodes);
            this.labelSelection
                ?.attr('fill', (node) => {
                    if (!this.selectedSurname || node.id === this.selectedSurname || secondaryNodes.has(node.id)) return '#000';
                    if (pathNodes.has(node.id)) return '#0066CC';
                    return '#AAA';
                })
                .filter(node => node.id === this.selectedSurname || secondaryNodes.has(node.id) || pathNodes.has(node.id))
                .raise();
        },
        highlightSurname() {
            if (!this.nodeSelection || !this.linkSelection) return;
            if (!this.selectedSurname) {
                this.secondaryNodes = [];
                this.pathNodes = [];
                this.nodeSelection
                    .classed('highlighted', false)
                    .classed('secondary', false)
                    .classed('dimmed', false)
                    .classed('path-to-thuemann', false)
                    .classed('thuemann-node', false);
                this.linkSelection
                    .classed('highlighted', false)
                    .classed('secondary', false)
                    .classed('dimmed', false)
                    .classed('path-to-thuemann', false);
                this.refreshVisibleLabels();
                return;
            }

            const links = this.linkSelection.data();
            const secondary = new Set();
            for (const link of links) {
                if (link.source.id === this.selectedSurname) secondary.add(link.target.id);
                if (link.target.id === this.selectedSurname) secondary.add(link.source.id);
            }
            const pathResult = this.findPathToThuemann(this.selectedSurname, links);
            const path = pathResult.path;
            const pathSet = new Set(path);
            const pathEdges = new Set();
            for (let index = 0; index < path.length - 1; index += 1) {
                pathEdges.add(`${path[index]}-${path[index + 1]}`);
                pathEdges.add(`${path[index + 1]}-${path[index]}`);
            }
            const isPathLink = link => pathEdges.has(`${link.source.id}-${link.target.id}`);
            this.secondaryNodes = [...secondary].sort((a, b) => a.localeCompare(b, 'nl', { sensitivity: 'base' }));
            this.pathNodes = path.filter(node => node !== this.selectedSurname);

            this.nodeSelection
                .classed('highlighted', node => node.id === this.selectedSurname)
                .classed('secondary', node => secondary.has(node.id) && !pathSet.has(node.id))
                .classed('path-to-thuemann', node => pathSet.has(node.id)
                    && node.id !== this.selectedSurname && node.id !== pathResult.targetReached)
                .classed('thuemann-node', node => node.id === pathResult.targetReached)
                .classed('dimmed', node => !secondary.has(node.id)
                    && node.id !== this.selectedSurname && !pathSet.has(node.id));
            this.linkSelection
                .classed('highlighted', link => link.source.id === this.selectedSurname || link.target.id === this.selectedSurname)
                .classed('path-to-thuemann', isPathLink)
                .classed('secondary', link => !isPathLink(link)
                    && (secondary.has(link.source.id) || secondary.has(link.target.id)))
                .classed('dimmed', link => !isPathLink(link)
                    && link.source.id !== this.selectedSurname && link.target.id !== this.selectedSurname);
            this.refreshVisibleLabels();
        },
        teardownGraph() {
            this.simulation?.stop();
            this.simulation?.on('tick', null).on('end', null);
            this.simulation = null;
            if (this.positionRaf) cancelAnimationFrame(this.positionRaf);
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.positionRaf = null;
            this.resizeRaf = null;
            this.outerSvg?.interrupt();
            this.outerSvg?.selectAll('*').interrupt();
            this.outerSvg?.on('.zoom', null).on('click', null);
            this.outerSvg?.remove();
            this.outerSvg = null;
            this.graphRoot = null;
            this.nodeSelection = null;
            this.linkSelection = null;
            this.labelSelection = null;
        },
    },
};
</script>

<style>
.surname-list-container h1 {
    font-family: var(--font-primary);
    /* color: var(--brand-text-dark); */
    color: var(--brand-color-1);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%;
    text-transform: lowercase;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.surname-tree-container {
    position: relative;
    height: 100vh !important;
    width: 100% !important;
    overflow: visible;
}

.surname-tree-container:hover {
    cursor: grab;
}

.surname-tree-container:active {
    cursor: grabbing;
}

/* #controls {
    margin-bottom: 20px;
} */

#zoom-buttons {
    position: absolute;
    bottom: 24px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 100;
}

#zoom-buttons button {
    padding: 0;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0);
    border: none;
    border-radius: var(--border-radius-pill);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
}

#zoom-buttons button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button {
    position: absolute;
    top: 24px;
    right: 24px;
    height: 28px;
    width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: var(--border-radius-pill);
    box-shadow: 0 0 0 0 var(--brand-color-1);
    z-index: 100;
}

#fullscreen-button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#fullscreen-button img {
    width: 16px;
    height: 16px;
}

#toggle-sidebar-button {
    position: absolute;
    top: 50%;
    left: 342px;
    transform: translateY(-50%);
    padding: 8px;
    cursor: pointer;
    background-color: #fff;
    border: none;
    border-radius: var(--border-radius-md);
    transition: var(--transition-ease);
    box-shadow: var(--shadow-card-sm);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
}

#toggle-sidebar-button.sidebar-collapsed {
    left: 9px;
}

#toggle-sidebar-button:hover {
    background-color: #f0f0f0;
}

#toggle-sidebar-button img {
    width: 20px;
    height: 20px;
}

#surname-tree-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    /* background-color: red; */
}

#surname-tree-container.is-loading svg {
    opacity: 0;
    pointer-events: none;
}

.surname-tree-status {
    position: absolute;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.surname-tree-status--overlay {
    flex-direction: column;
    gap: 14px;
    background: rgba(7, 9, 14, 0.88);
    color: #ffffff;
}

.surname-tree-status--error {
    background: rgba(255, 255, 255, 0.94);
    color: #8f171d;
}

.surname-tree-progress {
    width: min(440px, calc(100vw - 56px));
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.surname-tree-progress__bar {
    width: 100%;
    height: 10px;
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    overflow: hidden;
}

.surname-tree-progress__fill {
    height: 100%;
    background: linear-gradient(90deg, rgba(174, 30, 37, 0.85) 0%, rgba(224, 123, 128, 0.95) 100%);
    transition: width 0.22s ease;
}

.surname-tree-progress__value {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.surname-tree-container .links line {
    stroke: #aaa;
    stroke-width: 1px;
}

.surname-tree-container .nodes circle {
    cursor: pointer;
    stroke: #fff;
    stroke-width: 1.5px;
}

.surname-tree-container .nodes circle:hover {
    stroke: black;
}

.surname-tree-container .labels text {
    font-family: Arial, sans-serif;
    pointer-events: none;
}

.surname-tree-container .highlighted {
    stroke: black !important;
    fill: #ae1e25;
    /* Strong color for highlighted nodes */
}

.surname-tree-container .secondary {
    stroke: black !important;
    fill: #FFCC33 !important;
    /* Highlight secondary nodes in yellow */
}

.surname-tree-container .dimmed {
    stroke: #ccc !important;
    fill: #ddd;
    z-index: 1;
    /* Lighter color for unhighlighted nodes */
}

.surname-tree-container .links line.highlighted {
    stroke: black !important;
    z-index: 10;
    /* Strong color for highlighted links */
}

.surname-tree-container .links line.path-to-thuemann {
    stroke: #0066CC !important;
    stroke-width: 3px !important;
    z-index: 15 !important;
    /* Blue color for path to Thümann/Thumann - overrides highlighted */
}

.surname-tree-container .links line.secondary {
    stroke: black !important;
    /* Yellow color for secondary links */
}

.surname-tree-container .links line.dimmed {
    stroke: #ddd !important;
    z-index: 1;
    /* Lighter color for unhighlighted links */
}

.surname-tree-container .nodes circle.path-to-thuemann {
    stroke: #0066CC !important;
    fill: #0066CC !important;
    stroke-width: 2px !important;
    z-index: 15;
    /* Blue color for path nodes */
}

.surname-tree-container .nodes circle.thuemann-node {
    stroke: #0066CC !important;
    fill: #0066CC !important;
    stroke-width: 2px !important;
    /* Blue fill for reached Thümann/Thumann node */
}

/* .surname-list-label {
    font-weight: 700;
} */

.surname-list-container {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 0;
    padding: 2px 0;
    background-color: #fff;
    width: 360px;
    max-width: min(360px, 86vw);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    box-shadow: var(--shadow-card-lg);
    transition: transform .3s ease, opacity .3s ease;
}

.surname-list-container.collapsed {
    transform: translateX(-100%);
    /* opacity: 0; */
    pointer-events: none;
}

.surname-item {
    box-sizing: border-box;
    min-height: 40px;
    position: relative;
    padding: 8px calc(24px + 28px + 8px) 8px 24px;
    cursor: pointer;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    /* display: flex; */
    /* flex-direction: row; */
    /* justify-content: space-between; */
}

.surname-name {
    display: block;
    padding-right: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.surname-list-search {
    position: sticky;
    top: 0;
    z-index: 11;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
}

.surname-list-search input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: var(--border-radius-md);
    padding: 8px 10px;
    font-size: 13px;
    outline: none;
}

.surname-list-search input:focus {
    border-color: var(--brand-color-1);
    box-shadow: 0 0 0 2px rgba(174, 30, 37, 0.15);
}

.surname-item:hover .search-button {
    display: inline-block;
}

.search-button {
    display: none;
    /* Hidden by default */
    background-color: var(--brand-color-1);
    /* color: ; */
    border: none;
    border-radius: 4px;
    padding: 6px;
    cursor: pointer;
    /* font-size: 12px; */
    transition: background-color 0.2s ease;
    position: absolute;
    right: 12px;
    top: 6px;
    /* transform: translateY(-50%); */
}

.search-button:hover {
    background-color: #800000;
    /* Darker shade for hover */
}

.surname-item:hover .dropdown-toggle {
    display: inline-block;
}

.surname-tree-container .dropdown {
    position: absolute;
    right: 24px;
    top: 6px;
    display: inline-block;
}

.surname-tree-container .dropdown-toggle {
    display: none;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: 4px;
    padding: 6px 9px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.surname-tree-container .dropdown-toggle:hover {
    background-color: var(--brand-color-2);
}

.surname-tree-container .dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: -12px;
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    border-radius: 16px;
    z-index: 1000;
    padding: 8px 0;
    overflow: hidden;
}

.surname-tree-container .dropdown:hover .dropdown-menu {
    display: block;
}

.surname-tree-container .dropdown-menu button {
    width: 100%;
    padding: 8px 24px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
}

.surname-tree-container .dropdown-menu button:hover {
    background-color: #f5f5f5;
    text-decoration: underline;
}

.surname-item:last-child {
    border-bottom: none;
}

.surname-item:hover {
    background-color: #f0f0f0;
    text-decoration: underline;
}

.surname-item.highlighted {
    background-color: #f0f0f0;
    /* Highlight color */
    /* color: white; */
    color: var(--brand-color-1);
    font-weight: bold;
    z-index: 10;
}

.surname-item.highlighted:hover {
    text-decoration: none;
}

.secondary-label,
.secondary-item {
    color: var(--brand-text-dark);
    font-weight: 400;
    font-size: 12px;
}

.secondary-list {
    /* margin-top: 8px; */
    padding-left: 0;
    list-style: none;
}

.secondary-item {
    /* margin-bottom: 4px; */
    /* cursor: pointer; */
    padding-left: 16px;
    position: relative;
}

.secondary-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--brand-color-3);
    border-radius: var(--border-radius-pill);
    border: 1px solid var(--brand-text-dark);
}

.path-label,
.path-item {
    color: var(--brand-text-dark);
    font-weight: 400;
    font-size: 12px;
}

.path-list {
    padding-left: 0;
    list-style: none;
}

.path-item {
    padding-left: 16px;
    position: relative;
}

.path-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: #0066CC;
    border-radius: var(--border-radius-pill);
    border: 1px solid #0066CC;
}

@media (max-width: 860px) {
    .surname-list-container {
        display: none;
    }

    #toggle-sidebar-button {
        display: none;
    }

    #fullscreen-button {
        top: 16px;
        right: 16px;
    }

    #zoom-buttons {
        bottom: 16px;
        right: 16px;
    }
}
</style>
